import React, { lazy, Suspense } from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation } from 'react-router-dom';
import Header from './components/Header/Header';
import Hero from './components/Hero/Hero';
import Story from './components/Story/Story';
import RSVPModal from './components/RSVPModal/RSVPModal';
import CookieBanner from './components/CookieBanner/CookieBanner';
import { SHOW_PREMIERE_RSVP } from './config';
import './App.css';

// Lazy load below-the-fold sections and secondary pages for fast initial page load
const Trailer = lazy(() => import('./components/Trailer/Trailer'));
const Languages = lazy(() => import('./components/Languages/Languages'));
const Subscription = lazy(() => import('./components/Subscription/Subscription'));
const EditorialReview = lazy(() => import('./components/EditorialReview/EditorialReview'));
const Team = lazy(() => import('./components/Team/Team'));
const Footer = lazy(() => import('./components/Footer/Footer'));

const Success = lazy(() => import('./pages/Success'));
const Cancel = lazy(() => import('./pages/Cancel'));
const Register = lazy(() => import('./pages/Register'));
const Login = lazy(() => import('./pages/Login'));
const VerifyEmail = lazy(() => import('./pages/VerifyEmail'));
const Watch = lazy(() => import('./pages/Watch'));
const PrivacyPolicy = lazy(() => import('./pages/PrivacyPolicy'));
const Terms = lazy(() => import('./pages/Terms'));
const Contact = lazy(() => import('./pages/Contact'));
const About = lazy(() => import('./pages/About'));
const Profile = lazy(() => import('./pages/Profile'));
const ForgotPassword = lazy(() => import('./pages/ForgotPassword'));
const ResetPassword = lazy(() => import('./pages/ResetPassword'));
const ComingSoon = lazy(() => import('./pages/ComingSoon'));
const Articles = lazy(() => import('./pages/Articles'));
const ArticleDetail = lazy(() => import('./pages/ArticleDetail'));
const Reviews = lazy(() => import('./pages/Reviews'));
const InviteSignup = lazy(() => import('./pages/InviteSignup'));

const HomePage = () => (
  <>
    <Hero />
    <Story />
    <Suspense fallback={null}>
      <Trailer />
      <Languages />
      <Subscription />
      <EditorialReview />
      <Team />
    </Suspense>
  </>
);

function AppContent() {
  const location = useLocation();
  const isHomePage = location.pathname === '/';
  const isComingSoonPath = location.pathname === '/coming-soon';
  const [isRSVPOpen, setIsRSVPOpen] = React.useState(false);

  React.useEffect(() => {
    if (SHOW_PREMIERE_RSVP && isHomePage) {
      const isDismissed = sessionStorage.getItem('rsvp_dismissed') === 'true';
      if (!isDismissed) {
        const timer = setTimeout(() => {
          setIsRSVPOpen(true);
        }, 8000);
        return () => clearTimeout(timer);
      }
    }
  }, [isHomePage]);

  const handleCloseRSVP = () => {
    setIsRSVPOpen(false);
    sessionStorage.setItem('rsvp_dismissed', 'true');
  };

  return (
    <div className="App">
      {!isComingSoonPath && <Header onOpenRSVP={() => setIsRSVPOpen(true)} />}
      <Suspense fallback={null}>
        <Subscription isModalOnly={true} />
      </Suspense>
      {SHOW_PREMIERE_RSVP && (
        <RSVPModal isOpen={isRSVPOpen} onClose={handleCloseRSVP} />
      )}
      <main>
        <Suspense fallback={<div className="loading-spinner-screen" />}>
          <Routes>
            <Route path="/" element={<HomePage />} />
            <Route path="/coming-soon" element={<ComingSoon />} />
            <Route path="/register" element={<Register />} />
            <Route path="/login" element={<Login />} />
            <Route path="/verify-email" element={<VerifyEmail />} />
            <Route path="/verify-email/:token" element={<VerifyEmail />} />
            <Route path="/watch" element={<Watch />} />
            <Route path="/profile" element={<Profile />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/reset-password/:uid/:token" element={<ResetPassword />} />
            <Route path="/articles" element={<Articles />} />
            <Route path="/articles/:slug" element={<ArticleDetail />} />
            <Route path="/videos" element={<section><h2>Videos Gallery</h2><p>Coming Soon...</p></section>} />
            <Route path="/podcasts" element={<section><h2>Podcast Player</h2><p>Coming Soon...</p></section>} />
            <Route path="/success" element={<Success />} />
            <Route path="/cancel" element={<Cancel />} />
            <Route path="/privacy-policy" element={<PrivacyPolicy />} />
            <Route path="/terms" element={<Terms />} />
            <Route path="/contact" element={<Contact />} />
            <Route path="/about" element={<About />} />
            <Route path="/reviews" element={<Reviews />} />
            <Route path="/invite/:token" element={<InviteSignup />} />


          </Routes>
        </Suspense>
      </main>
      {!isComingSoonPath && (
        <Suspense fallback={null}>
          <Footer />
        </Suspense>
      )}
      <CookieBanner />
    </div>
  );
}

function App() {
  return (
    <Router>
      <AppContent />
    </Router>
  );
}

export default App;
